import Safe from "@safe-global/protocol-kit"
import { hashSafeMessage } from "@safe-global/protocol-kit/dist/src/utils/eip-712"
import { getEip3770NetworkPrefixFromChainId } from "@safe-global/protocol-kit/dist/src/utils/eip-3770"
import { useWallet as useSolanaWallet } from "@solana/wallet-adapter-react"
import bs58 from "bs58"
import { useCallback } from "react"
import { SiweMessage } from "siwe"
import invariant from "tiny-invariant"
import { useNetwork, useProvider } from "wagmi"
import { EXTERNAL_ROUTES } from "@/common/constants/routes"
import { fetchNonce } from "@/common/helpers/fetch"
import { SignInType } from "@/query/graphql"
import { useLogin } from "@/session/hooks/useLogin"
import { Chain, useMultichainAccount } from "@/web3/hooks/useMultichainAccount"
import { createSignInMessageText } from "@/web3/hooks/useSiws"
import { useSignerStore } from "@/web3/providers/SignerProvider"

export const useServices = () => {
  const { multichainAccount } = useMultichainAccount()
  const { address } = multichainAccount

  const { signer } = useSignerStore()
  const provider = useProvider()
  const { chain: wagmiChain } = useNetwork()
  const { signMessage } = useSolanaWallet()
  const { login } = useLogin()

  const checkIsSmartContractWalletService = useCallback(
    async (context: { chain: Chain | null }) => {
      const { chain } = context

      if (chain === Chain.SOL) {
        return false
      }

      const code = await provider.getCode(address)

      return code !== "0x"
    },
    [address, provider],
  )

  const fetchNonceService = useCallback(async () => {
    const nonceResponse = await fetchNonce()
    invariant(nonceResponse, "Nonce response is required")

    return nonceResponse
  }, [])

  const generateMessageService = useCallback(
    async (context: {
      nonceResult: {
        nonce: string
        expirationTime: string
        issuedAt: string
      } | null
      chain: Chain | null
    }) => {
      const { nonceResult, chain } = context
      invariant(nonceResult, "Nonce result is required")
      const { nonce, expirationTime, issuedAt } = nonceResult

      if (chain === Chain.SOL) {
        const siwsMessage = createSignInMessageText({
          domain: window.location.host,
          address,
          uri: process.env.NEXT_PUBLIC_TALLY_SIWE_URI,
          nonce,
          issuedAt,
          expirationTime,
        })

        return new TextEncoder().encode(siwsMessage)
      }

      const chainId = wagmiChain?.id
      invariant(chainId, "chainId must be defined")

      const siweMessage = new SiweMessage({
        uri: process.env.NEXT_PUBLIC_TALLY_SIWE_URI,
        nonce,
        expirationTime,
        issuedAt,
        domain: window.location.host,
        address,
        version: "1",
        chainId,
        statement:
          "Sign in with Ethereum to Tally and agree to the Terms of Service at terms.tally.xyz",
      })

      return siweMessage.prepareMessage()
    },
    [address, wagmiChain],
  )

  const generateSafeTransactionUrlService = useCallback(
    async (context: { message: string | Uint8Array | null }) => {
      const { message } = context
      invariant(message && typeof message === "string", "Message is required")

      invariant(wagmiChain, "Wagmi chain is required")

      const chainId = wagmiChain.id
      const rpcUrl = wagmiChain.rpcUrls.default.http[0]

      const safe = await Safe.init({
        safeAddress: address,
        provider: rpcUrl,
      })
      const messageHash = await safe.getSafeMessageHash(
        hashSafeMessage(message),
      )
      const safeChainPrefix = getEip3770NetworkPrefixFromChainId(
        BigInt(chainId),
      )

      return EXTERNAL_ROUTES.safe.msg(safeChainPrefix, address, messageHash)
    },
    [address, wagmiChain],
  )

  const signMessageService = useCallback(
    async (context: {
      message: string | Uint8Array | null
      chain: Chain | null
    }) => {
      const { message, chain } = context
      invariant(message, "Message is required")

      if (chain === Chain.SOL) {
        invariant(signMessage, "Sign message is required")
        invariant(message instanceof Uint8Array, "Message must be a Uint8Array")
        const signature = await signMessage(message)

        return bs58.encode(signature as Uint8Array)
      }

      invariant(signer, "Signer is required")

      const signature = await signer.signMessage(message)

      if (!signature.startsWith("0x")) {
        throw new Error("Invalid signature")
      }

      return signature
    },
    [signMessage, signer],
  )

  const loginService = useCallback(
    async (context: {
      message: string | Uint8Array | null
      signature: string | null
      nonceResult: { nonceToken: string } | null
      redirectTo: string | undefined
      chain: Chain | null
    }) => {
      const { message, signature, nonceResult, redirectTo, chain } = context
      invariant(message, "Message is required")
      invariant(signature, "Signature is required")
      invariant(nonceResult, "Nonce result is required")

      if (chain === Chain.SOL) {
        invariant(message instanceof Uint8Array, "Message must be a Uint8Array")

        const binaryString = Array.from(message)
          .map((byte) => String.fromCharCode(byte))
          .join("")

        await login(
          window.btoa(binaryString),
          signature,
          nonceResult.nonceToken,
          SignInType.Solana,
          redirectTo,
        )
      } else {
        invariant(typeof message === "string", "Message must be a string")
        await login(
          message,
          signature,
          nonceResult.nonceToken,
          SignInType.Evm,
          redirectTo,
        )
      }

      // Hacky solution to handle the fact this service resolves before `me` is updated in the identity provider.
      // Without waiting, the identity provider will trigger a `LOGGED_OUT` event.
      // In a future refactor, `me` can be defined in the identity provider and this can be removed.
      const wait = (ms: number) =>
        new Promise((resolve) => setTimeout(resolve, ms))
      await wait(1000)
    },
    [login],
  )

  return {
    checkIsSmartContractWallet: checkIsSmartContractWalletService,
    fetchNonce: fetchNonceService,
    generateMessage: generateMessageService,
    generateSafeTransactionUrl: generateSafeTransactionUrlService,
    signMessage: signMessageService,
    login: loginService,
  }
}
